<script setup>
    import { computed, ref } from 'vue';
    import MarkdownEditorModal from '@/shared/components/MarkdownEditorModal.vue';
    import ModalBase from '@/shared/components/ModalBase.vue';
    import HelpIcon from '@/shared/components/HelpIcon.vue';
    import useBuefy from '@/shared/composables/useBuefy.ts';
    import useShow from '@/shared/composables/useShow.js';

    const buefy = useBuefy();
    const { showInfo, showError } = useShow();

    const isModalActive = ref(false);

    const isMarkdownModalActive = ref(false);
    const markdownValue = ref('');

    const referenceLinks = [
        {
            label: 'Bulma',
            version: 'bulma 1.0.4',
            url: 'https://bulma.io/documentation/',
            description: 'Layout, typography, colors and helper classes. Our preferred source for standard CSS classes.'
        },
        {
            label: 'Buefy',
            version: 'buefy 3.0.11',
            url: 'https://buefy.org/documentation/',
            description: 'The b-* components, with their props, slots and events. Check here before writing custom markup.'
        },
        {
            label: 'Material Design Icons',
            version: '@mdi/font 7.4',
            url: 'https://pictogrammers.com/library/mdi/',
            description: 'Searchable catalogue of every icon name accepted by icon="..." props.'
        }
    ];

    const colorGroups = [
        {
            title: 'Brand',
            swatches: [
                { name: 'eq-primary', dark: false },
                { name: 'eq-prominent', dark: true },
                { name: 'eq-secondary', dark: true },
                { name: 'eq-additional-1', dark: true },
                { name: 'eq-additional-2', dark: false },
                { name: 'eq-additional-3', dark: false }
            ]
        },
        {
            title: 'Status and surfaces',
            swatches: [
                { name: 'eq-bg-highlight', dark: false },
                { name: 'eq-warning-resting', dark: false },
                { name: 'eq-energy-red', dark: false }
            ]
        },
        {
            title: 'North Sea',
            swatches: [
                { name: 'eq-north-sea-1', dark: true },
                { name: 'eq-north-sea-2', dark: true },
                { name: 'eq-north-sea-3', dark: true },
                { name: 'eq-north-sea-4', dark: false },
                { name: 'eq-north-sea-5', dark: false },
                { name: 'eq-north-sea-6', dark: false }
            ]
        },
        {
            title: 'Norwegian Woods',
            swatches: [
                { name: 'eq-norwegian-woods-1', dark: true },
                { name: 'eq-norwegian-woods-2', dark: false },
                { name: 'eq-norwegian-woods-3', dark: false },
                { name: 'eq-norwegian-woods-4', dark: false },
                { name: 'eq-norwegian-woods-5', dark: false },
                { name: 'eq-norwegian-woods-6', dark: false }
            ]
        },
        {
            title: 'Sand and Summer',
            swatches: [
                { name: 'eq-sand-and-summer-1', dark: true },
                { name: 'eq-sand-and-summer-2', dark: false },
                { name: 'eq-sand-and-summer-3', dark: false },
                { name: 'eq-sand-and-summer-4', dark: false },
                { name: 'eq-sand-and-summer-5', dark: false },
                { name: 'eq-sand-and-summer-6', dark: false }
            ]
        },
        {
            title: 'Midnight Sun and Autumn Storm',
            swatches: [
                { name: 'eq-midnight-sun-1', dark: false },
                { name: 'eq-midnight-sun-2', dark: false },
                { name: 'eq-autumn-storm-1', dark: false },
                { name: 'eq-autumn-storm-2', dark: false },
                { name: 'eq-autumn-storm-3', dark: false }
            ]
        }
    ];

    const textColors = ['eq-text-primary', 'eq-text-secondary', 'eq-text-tertiary'];

    const sharedComponents = [
        { name: 'ModalBase', purpose: 'Modal card with header/body/footer slots and the house button placement.' },
        { name: 'Spinner', purpose: 'Hides or replaces its slot content while loading.' },
        { name: 'HelpIcon', purpose: 'Help icon with a tooltip, for explaining a field or a concept.' },
        { name: 'TypeIcon', purpose: 'Icon plus tooltip for a library type (global, facility, virtual, uppercase, readonly, relation).' },
        { name: 'FavouriteIcon', purpose: 'Toggleable favourite star.' },
        { name: 'Breadcrumbs', purpose: 'Breadcrumb trail for nested pages.' },
        { name: 'ItemMetadata', purpose: 'Standard block of created/updated/owner metadata for an entity.' },
        { name: 'TagList, TagFilterList, TagFilterColumns', purpose: 'Render tags and filter content by tag.' },
        { name: 'DateTimePicker', purpose: 'Combined date and time input.' },
        { name: 'LibrarySelector, SingleScopeSelector, MultiScopeSelector, SetSelector, CodeRefSelector', purpose: 'Domain pickers. Use these instead of building a new autocomplete.' },
        { name: 'GenericTableSelect, GenericSearchAndSelect', purpose: 'Generic search-and-pick surfaces when no domain picker fits.' },
        { name: 'MarkdownEditorModal, MarkdownEditorComponent, VueMarkdown', purpose: 'Edit and render markdown.' },
        { name: 'SqlEditControl, SqlFilterEditControl', purpose: 'CodeMirror based SQL editing.' },
        { name: 'ExportButton, PublishCodeSetButton, SpecializedUiButton', purpose: 'Recurring domain actions with their own icon and confirmation behaviour.' }
    ];

    const demoTableData = [
        { name: 'Tag', type: 'Global', codes: 1240, state: 'Approved' },
        { name: 'Discipline', type: 'Global', codes: 31, state: 'Approved' },
        { name: 'ProjectMaster', type: 'Facility', codes: 0, state: 'Draft' }
    ];

    const demoName = ref('');
    const demoNameTouched = ref(false);
    const demoLibrary = ref(null);
    const demoIncludeDeleted = ref(false);

    const demoNameMessage = computed(() => (
        demoNameTouched.value && !demoName.value.trim() ? 'Name is required' : null
    ));

    function showDemoConfirm() {
        buefy.dialog.confirm({
            title: 'Delete library',
            message: 'This permanently deletes the library and all its codes. Continue?',
            confirmText: 'Delete',
            type: 'is-danger',
            hasIcon: true,
            onConfirm: () => showInfo('The library was deleted')
        });
    }

    const iconTypes = [
        { type: 'is-default', description: 'What you want most of the time' },
        { type: 'is-primary', description: '' },
        { type: 'is-info', description: '' },
        { type: 'is-danger', description: '' },
        { type: 'is-warning', description: '' }
    ];

    const iconSizes = ['is-small', 'is-default', 'is-medium', 'is-large'];

    const iconographyIcons = [
        {
            title: 'Frequently used',
            description: 'Common actions and status icons used across the UI.',
            icons: [
                { icon: 'alert', description: '', deprecated: false },
                { icon: 'alert-circle', description: '', deprecated: false },
                { icon: 'alert-circle-outline', description: '', deprecated: false },
                { icon: 'alert-outline', description: '', deprecated: false },
                { icon: 'check', description: '', deprecated: false },
                { icon: 'check-circle', description: '', deprecated: false },
                { icon: 'checkbox-marked-circle-outline', description: '', deprecated: false },
                { icon: 'cog', description: 'Settings/options', deprecated: false },
                { icon: 'help', description: '', deprecated: false },
                { icon: 'help-circle', description: '', deprecated: false },
                { icon: 'help-circle-outline', description: '', deprecated: false },
                { icon: 'history', description: '', deprecated: false },
                { icon: 'information', description: '', deprecated: false },
                { icon: 'information-outline', description: '', deprecated: false },
                { icon: 'table', description: '', deprecated: false },
                { icon: 'loading', description: 'Add spin class to make it spin', deprecated: false }

            ]
        },
        {
            title: 'Frequently used actions',
            description: 'Common actions used across the UI.',
            icons: [
                { icon: 'content-copy', description: '', deprecated: false },
                { icon: 'close', description: '', deprecated: false },
                { icon: 'close-circle', description: '', deprecated: false },
                { icon: 'close-circle-outline', description: '', deprecated: false },
                { icon: 'magnify', description: 'Indicate search', deprecated: false },
                { icon: 'trash-can', description: '', deprecated: false },
                { icon: 'trash-can-outline', description: '', deprecated: false },
                { icon: 'floppy', description: 'Save (to database)', deprecated: false },
                { icon: 'pencil', description: 'Edit', deprecated: false },
                { icon: 'pencil-box', description: '', deprecated: true },
                { icon: 'download', description: '', deprecated: false },
                { icon: 'upload', description: '', deprecated: false },
                { icon: 'delete', description: '', deprecated: false },
                { icon: 'delete-outline', description: '', deprecated: false },
                { icon: 'play', description: 'Run some action', deprecated: false },
                { icon: 'play-circle-outline', description: '', deprecated: false },
                { icon: 'plus', description: '', deprecated: false },
                { icon: 'plus-circle', description: '', deprecated: false },
                { icon: 'plus-circle-outline', description: '', deprecated: false },
                { icon: 'refresh', description: '', deprecated: false },
                { icon: 'reload', description: '', deprecated: false },
                { icon: 'replay', description: '', deprecated: false },
                { icon: 'restore', description: '', deprecated: false },
                { icon: 'open-in-new', description: '', deprecated: false },
                { icon: 'eraser', description: 'Remove (temporary) content', deprecated: false },
                { icon: 'undo', description: '', deprecated: false }

            ]
        },
        {
            title: 'File types and formats',
            description: 'Icons representing specific file types, data formats, or document states.',
            icons: [
                { icon: 'file-word', description: '', deprecated: false },
                { icon: 'file-excel', description: '', deprecated: false },
                { icon: 'script-text-outline', description: 'SQL script/query', deprecated: false }

            ]
        },
        {
            title: 'Features',
            description: 'Icons used in feature-specific flows and pages.',
            icons: [
                { icon: 'bookshelf', description: 'All libraries', deprecated: false },
                { icon: 'bullhorn-outline', description: 'Our changelog', deprecated: false },
                { icon: 'database-search', description: 'SQL query (editor)', deprecated: false },
                { icon: 'chart-areaspline', description: 'Usage statistics', deprecated: false },
                { icon: 'chart-arc', description: '', deprecated: false },
                { icon: 'chart-areaspline-variant', description: '', deprecated: false },
                { icon: 'chart-bar', description: '', deprecated: false },
                { icon: 'chart-tree', description: '', deprecated: false },
                { icon: 'trophy', description: 'KPI report', deprecated: false },
                { icon: 'sail-boat', description: 'Shipping', deprecated: false },
                { icon: 'graph', description: '', deprecated: false },
                { icon: 'truck', description: 'Changelog', deprecated: false },
                { icon: 'rocket-launch', description: 'Publishing', deprecated: false },
                { icon: 'crane', description: 'Release', deprecated: false },
                { icon: 'earth', description: 'Global library', deprecated: false },
                { icon: 'paperclip', description: 'Attachment', deprecated: false },
                { icon: 'alphabetical-variant', description: 'Uppercase library', deprecated: false },
                { icon: 'relation-many-to-many', description: 'Relation library', deprecated: false },
                { icon: 'alpha-v-circle', description: 'Virtual library', deprecated: false },
                { icon: 'factory', description: 'Scoped by Facility', deprecated: false },
                { icon: 'star', description: 'Favorite', deprecated: false },
                { icon: 'file-lock-outline', description: 'Our documentation (currently locked)', deprecated: false },
                { icon: 'file-document-multiple-outline', description: 'Documentation', deprecated: false },
                { icon: 'file-import-outline', description: 'Excel import', deprecated: false }
            ]
        },
        {
            title: 'Specialized UI',
            description: 'Icons tied to specialized widgets or domain visuals.',
            icons: [
                { icon: 'cable-data', description: 'Cable UI', deprecated: false },
                { icon: 'pipe-valve', description: 'PipingClass UI', deprecated: false },
                { icon: 'ruler', description: 'Unit of Measurement UI', deprecated: false }

            ]
        },
        {
            title: 'Other',
            description: 'All documented icons. Move icons from here into the groups above.',
            icons: [
                { icon: 'account', description: 'Represent user', deprecated: false },
                { icon: 'account-group', description: 'Users', deprecated: false },
                { icon: 'account-hard-hat-outline', description: 'Operational notification', deprecated: false },
                { icon: 'archive', description: '', deprecated: false },
                { icon: 'arrow-left', description: '', deprecated: false },
                { icon: 'arrow-right', description: '', deprecated: false },
                { icon: 'arrow-right-bold', description: '', deprecated: false },
                { icon: 'auto-fix', description: '', deprecated: false },
                { icon: 'book', description: '', deprecated: false },
                { icon: 'book-open-variant', description: '', deprecated: false },
                { icon: 'calendar-today', description: '', deprecated: false },
                { icon: 'chevron-down', description: '', deprecated: false },
                { icon: 'chevron-left', description: '', deprecated: false },
                { icon: 'chevron-right', description: '', deprecated: false },
                { icon: 'chevron-up', description: '', deprecated: false },
                { icon: 'clock', description: '', deprecated: false },
                { icon: 'cloud', description: '', deprecated: false },
                { icon: 'comment', description: '', deprecated: false },
                { icon: 'content-duplicate', description: '', deprecated: false },
                { icon: 'database', description: '', deprecated: false },
                { icon: 'drag', description: '', deprecated: false },
                { icon: 'emoticon-sad', description: '', deprecated: false },
                { icon: 'emoticon-sad-outline', description: '', deprecated: false },
                { icon: 'file', description: '', deprecated: false },
                { icon: 'file-document', description: '', deprecated: false },
                { icon: 'file-document-check-outline', description: '', deprecated: false },
                { icon: 'file-document-outline', description: '', deprecated: false },
                { icon: 'file-replace', description: '', deprecated: false },
                { icon: 'filter', description: '', deprecated: false },
                { icon: 'folder', description: '', deprecated: false },
                { icon: 'folder-open', description: '', deprecated: false },
                { icon: 'format-list-bulleted', description: '', deprecated: false },
                { icon: 'format-list-numbered', description: '', deprecated: false },
                { icon: 'forward', description: '', deprecated: false },
                { icon: 'hand-back-right-outline', description: '', deprecated: false },
                { icon: 'link-variant', description: '', deprecated: false },
                { icon: 'lock', description: 'Locked state (readonly library)', deprecated: false },
                { icon: 'lock-off', description: '', deprecated: false },
                { icon: 'lock-open-variant-outline', description: '', deprecated: false },
                { icon: 'menu-down', description: '', deprecated: false },
                { icon: 'menu-up', description: '', deprecated: false },
                { icon: 'minus', description: '', deprecated: false },
                { icon: 'pin', description: 'Pin scope, etc.', deprecated: false },
                { icon: 'pin-outline', description: 'Pin scope, etc.', deprecated: false },
                { icon: 'rss', description: 'Legacy query builder', deprecated: true },
                { icon: 'script', description: '', deprecated: true },
                { icon: 'select-all', description: '', deprecated: false },
                { icon: 'select-inverse', description: '', deprecated: false },
                { icon: 'select-off', description: '', deprecated: false },
                { icon: 'server', description: '', deprecated: false },
                { icon: 'share', description: '', deprecated: false },
                { icon: 'share-variant', description: '', deprecated: false },
                { icon: 'skull', description: 'Dangerous action', deprecated: false },
                { icon: 'square', description: '', deprecated: false },
                { icon: 'stop', description: '', deprecated: false },
                { icon: 'table-arrow-right', description: '', deprecated: false },
                { icon: 'table-column', description: '', deprecated: false },
                { icon: 'table-multiple', description: '', deprecated: false },
                { icon: 'table-plus', description: '', deprecated: false },
                { icon: 'table-search', description: '', deprecated: false },
                { icon: 'thumb-down', description: '', deprecated: false },
                { icon: 'thumb-up', description: '', deprecated: false },
                { icon: 'timer-sand', description: '', deprecated: false },
                { icon: 'unfold-less-horizontal', description: '', deprecated: false }
            ]
        }
    ];

    const sectionGroups = [
        {
            label: 'Foundations',
            items: [
                { id: 'section-principles', label: 'Principles' },
                { id: 'section-colors', label: 'Colors' },
                { id: 'section-typography', label: 'Typography' },
                { id: 'section-layout', label: 'Layout and spacing' }
            ]
        },
        {
            label: 'Components',
            items: [
                { id: 'section-buttons', label: 'Buttons' },
                { id: 'section-forms', label: 'Forms and inputs' },
                { id: 'section-tables', label: 'Tables' },
                { id: 'section-modals', label: 'Modals' },
                { id: 'section-navigation', label: 'Navigation and disclosure' }
            ]
        },
        {
            label: 'Patterns',
            items: [
                { id: 'section-feedback', label: 'Feedback and status' },
                { id: 'section-shared-components', label: 'Shared components' }
            ]
        },
        {
            label: 'Reference',
            items: [
                { id: 'section-references', label: 'External documentation' },
                { id: 'section-iconography', label: 'Iconography' }
            ]
        }
    ];

    function scrollToSection(id) {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
</script>

<template>
    <div class="columns">
        <div class="column is-one-quarter is-align-self-flex-start section-menu">
            <b-menu>
                <b-menu-list
                    v-for="group in sectionGroups"
                    :key="group.label"
                    :label="group.label">
                    <b-menu-item
                        v-for="menuItem in group.items"
                        :key="menuItem.id"
                        :label="menuItem.label"
                        tag="a"
                        @click="scrollToSection(menuItem.id)" />
                </b-menu-list>
            </b-menu>
        </div>

        <div class="column">
            <h1 class="title">
                UI Design System
            </h1>
            <p class="block">
                This page describes how we use Bulma and Buefy in the Common Library user interface. It is a house style
                guide rather than a component library: it records the conventions we have agreed on and leaves the
                exhaustive component reference to the upstream documentation.
            </p>
            <p class="block">
                When you build UI, start from the closest existing page and follow the patterns below. If you need
                something that is not described here, use the plain Bulma or Buefy default instead of inventing a new
                variant, and consider documenting it here afterwards. Every example below is live, so you can see and
                interact with the real components, and the page itself is written to the conventions it describes, so
                its source is a worked example too.
            </p>

            <h2
                id="section-principles"
                class="title is-4 mt-6 section-header">
                Principles
            </h2>
            <ul class="bullet-list block">
                <li>
                    <b>Lean on Bulma and Buefy.</b> Reach for an existing component and its documented props before
                    writing custom markup or CSS. Custom styling is a last resort, and should be scoped to the component
                    that needs it.
                </li>
                <li>
                    <b>Do not introduce new design tokens.</b> The palette, spacing scale and typography are fixed. Use
                    the existing <code>--eq-*</code> variables and Bulma helpers; do not add new colors or fonts unless
                    it has been explicitly agreed.
                </li>
                <li>
                    <b>Prefer <code>b-*</code> components over native elements.</b> Use <code>&lt;b-button&gt;</code>,
                    <code>&lt;b-input&gt;</code>, <code>&lt;b-table&gt;</code> and friends. Native
                    <code>&lt;button&gt;</code> or <code>&lt;input&gt;</code> only when the native semantics are
                    genuinely required.
                </li>
                <li>
                    <b>Use helper classes, not inline styles.</b> Bulma spacing, flex and text helpers
                    (<code>mt-3</code>, <code>is-flex</code>, <code>has-text-weight-bold</code>) express intent and stay
                    consistent. A hard-coded <code>style</code> attribute in a template is a code smell; a bound
                    <code>:style</code> is fine when the value is genuinely data-driven, as with the color swatches
                    below.
                </li>
                <li>
                    <b>Communicate meaning with the semantic types.</b> <code>is-primary</code>,
                    <code>is-info</code>, <code>is-warning</code> and <code>is-danger</code> carry meaning across
                    buttons, tags, notifications, toasts and icons. Keep that meaning consistent and never pick a type
                    purely because you like the color.
                </li>
                <li>
                    <b>Reuse before you build.</b> Check <i>Shared components</i> below before adding another selector,
                    modal or spinner.
                </li>
            </ul>

            <h2
                id="section-colors"
                class="title is-4 mt-6 section-header">
                Colors
            </h2>
            <p class="block">
                Colors come from the Equinor palette and are exposed as CSS variables in <code>css/colours.scss</code>.
                In practice you should rarely name a color at all: use the Bulma and Buefy semantic types, which are
                already mapped to the palette. When no semantic type applies, use a <code>var(--eq-*)</code> variable,
                and never hard-code a hex value.
            </p>
            <p class="block">
                <code>eq-prominent</code> is our signature green and is used for links and highlights. The Equinor red
                draws attention to the single most important element on a screen, such as the save or run button; that
                is what <code>is-primary</code> gives you, so use it sparingly. Destructive actions use the darker red
                behind <code>is-danger</code> instead, so that danger is never confused with importance.
            </p>
            <div
                v-for="colorGroup in colorGroups"
                :key="colorGroup.title"
                class="block">
                <h3 class="title is-6">
                    {{ colorGroup.title }}
                </h3>
                <div class="grid is-col-min-7">
                    <div
                        v-for="swatch in colorGroup.swatches"
                        :key="swatch.name"
                        class="cell p-2 is-size-7"
                        :class="swatch.dark ? 'has-text-white' : 'has-text-black'"
                        :style="{ backgroundColor: `var(--${swatch.name})` }">
                        {{ swatch.name }}
                    </div>
                </div>
            </div>
            <div class="block">
                <h3 class="title is-6">
                    Text
                </h3>
                <div class="grid is-col-min-7">
                    <span
                        v-for="textColor in textColors"
                        :key="textColor"
                        class="cell is-size-7"
                        :style="{ color: `var(--${textColor})` }">
                        {{ textColor }}
                    </span>
                </div>
            </div>

            <h2
                id="section-typography"
                class="title is-4 mt-6 section-header">
                Typography
            </h2>
            <p class="block">
                We use the Bulma <code>title</code> and <code>subtitle</code> scale and nothing else. The page title is
                an <code>&lt;h1 class="title"&gt;</code> with no level, one per view. Section headings step down from
                there, typically <code>title is-4</code> for a section and <code>title is-5</code> or
                <code>is-6</code> for subsections and small group labels. <code>subtitle</code> is the
                short explanatory line under a title, not a heading in its own right.
            </p>
            <p class="block">
                Wrap longer prose, lists and generated markdown in a <code>content</code> element so that Bulma styles
                paragraphs and lists for you. For a free-standing bulleted list outside <code>content</code>, use our
                <code>bullet-list</code> class. Use <code>&lt;code&gt;</code> for identifiers, library names, attribute
                names and other values the user could type or copy.
            </p>
            <p class="is-flex is-flex-wrap-wrap is-gap-2 is-size-7 block">
                <a
                    href="https://bulma.io/documentation/elements/title/"
                    target="_blank"
                    rel="noopener">Bulma: title</a>
            </p>

            <h2
                id="section-layout"
                class="title is-4 mt-6 section-header">
                Layout and spacing
            </h2>
            <p class="block">
                Pages are built from Bulma layout primitives, kept shallow and predictable. The application shell wraps
                every routed view in a <code>section</code>, so most pages start at the heading; add a
                <code>container</code> yourself if the page should be width-constrained. Use <code>columns</code> for
                side-by-side content, preferring a few wide columns over a
                fine-grained grid and letting them stack on narrow screens; <code>grid</code> with <code>cell</code>
                children for a responsive set of equally sized items; <code>level</code> for a header row with a
                title on the left and actions on the right; and <code>box</code> to group related content, or
                <code>card</code> when the group needs its own header or footer. Do not nest boxes.
            </p>
            <p class="block">
                For spacing, use Bulma's helper classes: <code>block</code> for vertical rhythm between sibling blocks,
                the <code>m*</code> / <code>p*</code> helpers for one-off adjustments, and the flex and
                <code>is-gap-*</code> helpers instead of writing a new flex rule. Sizes come from the same scale, so
                prefer <code>is-size-7</code> over a hand-picked <code>font-size</code>. A handful of app-wide
                utilities live in <code>css/main.scss</code>, the most useful being <code>is-clickable</code> for
                clickable rows.
            </p>
            <p class="is-flex is-flex-wrap-wrap is-gap-2 is-size-7 block">
                <a
                    href="https://bulma.io/documentation/columns/"
                    target="_blank"
                    rel="noopener">Bulma: columns</a>
                <a
                    href="https://bulma.io/documentation/grid/"
                    target="_blank"
                    rel="noopener">Bulma: grid</a>
                <a
                    href="https://bulma.io/documentation/helpers/spacing-helpers/"
                    target="_blank"
                    rel="noopener">Bulma: spacing helpers</a>
            </p>

            <h2
                id="section-buttons"
                class="title is-4 mt-6 section-header">
                Buttons
            </h2>
            <p class="block">
                Use <code>&lt;b-button&gt;</code>. The <code>type</code> tells the user how much weight the action
                carries. Use <code>is-primary</code> for the primary action within a page region, form or modal, and
                avoid competing primary actions in the same group.
            </p>
            <table class="table is-narrow is-striped block">
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Use for</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td><code>is-primary</code></td>
                        <td>The main action of the page, form or modal. Save, run, create.</td>
                    </tr>
                    <tr>
                        <td><code>is-danger</code></td>
                        <td>Destructive and irreversible actions. Always confirm these first.</td>
                    </tr>
                    <tr>
                        <td><code>is-info</code></td>
                        <td>Supporting actions that are still worth noticing, such as export or download.</td>
                    </tr>
                    <tr>
                        <td>(no type)</td>
                        <td>Everything else, including cancel and close.</td>
                    </tr>
                </tbody>
            </table>
            <div class="block">
                <div class="buttons">
                    <b-button type="is-primary">
                        Important action
                    </b-button>
                    <b-button type="is-danger">
                        Dangerous action
                    </b-button>
                    <b-button type="is-info">
                        Secondary action
                    </b-button>
                    <b-button>
                        Other action
                    </b-button>
                </div>
            </div>
            <p class="block">
                Prefer an icon plus a verb label. Pass the icon with <code>icon-left</code> rather than nesting a
                <code>&lt;b-icon&gt;</code>; use <code>icon-right</code> only for buttons that open or expand something.
                Keep labels short and in sentence case. An icon-only button needs both a tooltip and an
                <code>aria-label</code>, since the tooltip does not name the button for assistive technology, and any
                button that triggers a request should get <code>:loading</code> so the user cannot submit twice.
            </p>
            <div class="block">
                <div class="buttons">
                    <b-button
                        type="is-primary"
                        icon-left="play">
                        Run
                    </b-button>
                    <b-button
                        type="is-danger"
                        icon-left="trash-can">
                        Delete
                    </b-button>
                    <b-button
                        type="is-info"
                        icon-left="download">
                        Download
                    </b-button>
                    <b-button icon-left="close-circle">
                        Cancel
                    </b-button>
                    <b-tooltip label="Edit">
                        <b-button
                            icon-left="pencil"
                            aria-label="Edit" />
                    </b-tooltip>
                    <b-button
                        type="is-primary"
                        loading>
                        Saving
                    </b-button>
                    <b-button disabled>
                        Disabled
                    </b-button>
                </div>
            </div>
            <p class="block">
                Sizes follow Bulma: <code>is-small</code> inside dense surfaces such as table rows,
                the default size everywhere else. Avoid <code>is-medium</code> and <code>is-large</code>.
            </p>

            <h3 class="title is-5">
                Grouping buttons
            </h3>
            <p class="block">
                Wrap related buttons in a container that owns the spacing, rather than leaving them as bare siblings
                where they end up touching each other. We use two groupings, both of which read as a small toolbar.
            </p>
            <p class="block">
                <b>Page header actions.</b> On entity pages such as Library, CodeSet and Code, the page title sits in
                one column and the actions in the next, wrapped in a <code>&lt;b-field position="is-right"&gt;</code>
                with one <code>&lt;p class="control"&gt;</code> per button. This right-aligns the group and keeps the
                buttons tight together, so they read as one bar of actions belonging to the entity. Put the least
                important action first and the primary action last, closest to the right edge.
            </p>
            <div class="box block">
                <div class="columns">
                    <div class="column">
                        <h1 class="title">
                            Some entity
                        </h1>
                    </div>
                    <div class="column">
                        <b-field position="is-right">
                            <p class="control">
                                <b-button icon-left="graph">
                                    Explore
                                </b-button>
                            </p>
                            <p class="control">
                                <b-button icon-left="magnify">
                                    Query
                                </b-button>
                            </p>
                            <p class="control">
                                <b-button
                                    type="is-info"
                                    icon-left="plus">
                                    New set
                                </b-button>
                            </p>
                            <p class="control">
                                <b-button
                                    type="is-primary"
                                    icon-left="pencil">
                                    Edit
                                </b-button>
                            </p>
                        </b-field>
                    </div>
                </div>
            </div>
            <p class="block">
                <b>Action bars.</b> When the actions operate on an editor or a result below them, as in the SQL editor,
                put them in a plain <code>&lt;div class="buttons"&gt;</code> above that content. The wrapper gives
                consistent spacing and wraps sensibly on narrow screens. Here the order runs the other way: the action
                the user reaches for most, typically the primary one, comes first.
            </p>
            <div class="box block">
                <div class="buttons">
                    <b-button
                        type="is-primary"
                        icon-left="play">
                        Run
                    </b-button>
                    <b-button icon-left="folder-open">
                        Load
                    </b-button>
                    <b-button icon-left="floppy">
                        Save
                    </b-button>
                    <b-button icon-left="share-variant">
                        Copy link
                    </b-button>
                    <b-button icon-left="download">
                        Export
                    </b-button>
                </div>
            </div>
            <p class="block">
                Keep either group to a handful of buttons. Once it grows past that, move the rarely used actions into a
                <code>b-dropdown</code> behind a single trigger. Destructive actions never sit next to the primary
                action, and an action that only applies to a selection should be disabled rather than hidden while
                nothing is selected.
            </p>
            <p class="is-flex is-flex-wrap-wrap is-gap-2 is-size-7 block">
                <a
                    href="https://buefy.org/documentation/button"
                    target="_blank"
                    rel="noopener">Buefy: button</a>
            </p>

            <h2
                id="section-forms"
                class="title is-4 mt-6 section-header">
                Forms and inputs
            </h2>
            <p class="block">
                Use <code>&lt;b-field&gt;</code> for form controls: it carries the label, the validation state and the
                help message, so you do not need your own label markup. Use
                <code>label-position="on-border"</code> for compact forms and inline filters, and the default stacked
                label for full editing forms.
            </p>
            <p class="block">
                Show validation errors on the field itself: set <code>type="is-danger"</code> and put the explanation in
                <code>message</code>. Validate as the user leaves the field rather than on every keystroke, and keep
                messages short and specific. Use <code>message</code> without a type for neutral help text, or a
                <code>HelpIcon</code> next to the label when the explanation is longer.
            </p>
            <div class="columns">
                <div class="column is-half">
                    <div class="box">
                        <b-field
                            label="Name"
                            :type="demoNameMessage ? 'is-danger' : ''"
                            :message="demoNameMessage">
                            <b-input
                                v-model="demoName"
                                placeholder="Library name"
                                @blur="demoNameTouched = true" />
                        </b-field>
                        <b-field
                            label="Library"
                            message="Neutral help text explaining the choice.">
                            <b-select
                                v-model="demoLibrary"
                                placeholder="Select a library"
                                expanded>
                                <option value="tag">
                                    Tag
                                </option>
                                <option value="discipline">
                                    Discipline
                                </option>
                            </b-select>
                        </b-field>
                        <b-field grouped>
                            <b-field
                                label="From"
                                label-position="on-border"
                                expanded>
                                <b-input placeholder="Code" />
                            </b-field>
                            <b-field
                                label="To"
                                label-position="on-border"
                                expanded>
                                <b-input placeholder="Code" />
                            </b-field>
                        </b-field>
                        <b-field>
                            <b-checkbox v-model="demoIncludeDeleted">
                                Include deleted
                            </b-checkbox>
                        </b-field>
                    </div>
                </div>
            </div>
            <p class="block">
                Pick the simplest control that fits: <code>b-select</code> for a handful of fixed options,
                <code>b-autocomplete</code> when the list is long or comes from the server, <code>b-taginput</code> for
                multiple values, <code>b-checkbox</code> for an option that only takes effect when the form is
                submitted, and <code>b-switch</code> for a setting that applies immediately. Let inputs fill their
                container with <code>expanded</code> instead of fixing widths. Before adding an autocomplete or picker,
                check the shared selectors under <i>Shared components</i>; most domain concepts already have one.
            </p>
            <p class="is-flex is-flex-wrap-wrap is-gap-2 is-size-7 block">
                <a
                    href="https://buefy.org/documentation/field"
                    target="_blank"
                    rel="noopener">Buefy: field</a>
            </p>

            <h2
                id="section-tables"
                class="title is-4 mt-6 section-header">
                Tables
            </h2>
            <p class="block">
                Tabular data uses <code>&lt;b-table&gt;</code> with <code>striped</code>, <code>narrowed</code> and
                <code>hoverable</code>. That is the house recipe and it should be your starting point. Declare columns
                with <code>&lt;b-table-column&gt;</code> so that formatting and links live in the template, and always
                provide an <code>#empty</code> slot: a table that renders nothing looks broken.
            </p>
            <b-table
                :data="demoTableData"
                striped
                narrowed
                hoverable
                class="block">
                <b-table-column
                    v-slot="props"
                    field="name"
                    label="Name"
                    sortable>
                    <a href="#">{{ props.row.name }}</a>
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="type"
                    label="Type"
                    sortable>
                    {{ props.row.type }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="codes"
                    label="Codes"
                    numeric
                    sortable>
                    {{ props.row.codes }}
                </b-table-column>
                <b-table-column
                    v-slot="props"
                    field="state"
                    label="State">
                    <b-tag :type="props.row.state === 'Approved' ? 'is-success' : 'is-warning'">
                        {{ props.row.state }}
                    </b-tag>
                </b-table-column>
                <template #empty>
                    <div class="has-text-centered has-text-grey">
                        No libraries found
                    </div>
                </template>
            </b-table>
            <p class="block">
                Everything beyond the basic recipe is opt-in and should be added only when the data calls for it:
                <code>paginated</code> with <code>:per-page</code> once a table can exceed a screenful (plus
                <code>backend-pagination</code> and <code>backend-sorting</code> when the server does the work),
                <code>sticky-header</code> for long tables, <code>checkable</code> only when a bulk action exists,
                <code>detailed</code> only when the detail does not fit in a row, and <code>:loading</code> while data
                is being fetched so the table keeps its place instead of collapsing.
            </p>
            <p class="block">
                Right-align numeric columns with <code>numeric</code>, put row actions in a trailing unlabelled column
                of small icon buttons, and keep the first column the identifying one, linking to the entity.
            </p>
            <p class="is-flex is-flex-wrap-wrap is-gap-2 is-size-7 block">
                <a
                    href="https://buefy.org/documentation/table"
                    target="_blank"
                    rel="noopener">Buefy: table</a>
            </p>

            <h2
                id="section-modals"
                class="title is-4 mt-6 section-header">
                Modals
            </h2>
            <p class="block">
                Use a modal for a focused task that the user must finish or abandon before continuing, typically
                creating or editing a single item. Anything larger belongs on its own page, and anything smaller is
                usually a confirmation dialog.
            </p>
            <p class="block">
                Build modals with the shared <code>ModalBase</code> component, which gives you the modal card, the
                header/body/footer slots and the correct button placement. Buttons go in the footer, right-aligned, with
                cancel on the left and the affirmative action on the right. Give the affirmative button a verb that says
                what happens (<i>Save</i>, <i>Add</i>, <i>Delete</i>), never <i>OK</i>. Closing a modal must discard
                nothing silently: either save, or warn about unsaved changes.
            </p>
            <div class="block">
                <div class="buttons">
                    <b-button
                        type="is-primary"
                        icon-left="play"
                        @click="isModalActive = true">
                        Launch modal
                    </b-button>
                    <b-button
                        icon-left="pencil"
                        @click="isMarkdownModalActive = true">
                        Launch markdown modal
                    </b-button>
                </div>
            </div>
            <markdown-editor-modal
                v-if="isMarkdownModalActive"
                :value="markdownValue"
                title="Edit some markdown value"
                show-preview
                @done="(v) => { isMarkdownModalActive = false; markdownValue = v; }" />
            <p class="is-flex is-flex-wrap-wrap is-gap-2 is-size-7 block">
                <a
                    href="https://buefy.org/documentation/modal"
                    target="_blank"
                    rel="noopener">Buefy: modal</a>
            </p>

            <h2
                id="section-navigation"
                class="title is-4 mt-6 section-header">
                Navigation and disclosure
            </h2>
            <p class="block">
                Use <code>b-tabs</code> for sibling views of the same entity, with one- or two-word labels, the most
                used tab first, and <code>TabBadge</code> when a tab needs a count. Use <code>b-collapse</code> for
                secondary content most users can ignore, such as filter panels and advanced options, collapsed by
                default unless the user has already made a choice inside. Use <code>b-dropdown</code> to group related
                actions behind one trigger, with destructive items last and separated. Reach for
                <code>b-pagination</code>, or the <code>paginated</code> prop on a table, rather than infinite
                scrolling, and add <code>Breadcrumbs</code> to pages reached by drilling down so the user can get back
                out.
            </p>
            <p class="block">
                Links are underlined and use the prominent green. Bold a link when it identifies the entity a row or
                card is about; leave it plain for supporting links. Links that leave the application open in a new tab
                and get an <code>open-in-new</code> icon.
            </p>
            <div class="block">
                <div>
                    <a href="#"><b>Link to some entity</b></a>
                </div>
                <div>
                    <a href="#">Link with less importance</a>
                </div>
            </div>
            <p class="is-flex is-flex-wrap-wrap is-gap-2 is-size-7 block">
                <a
                    href="https://buefy.org/documentation/tabs"
                    target="_blank"
                    rel="noopener">Buefy: tabs</a>
            </p>

            <h2
                id="section-feedback"
                class="title is-4 mt-6 section-header">
                Feedback and status
            </h2>
            <p class="block">
                Choose the least intrusive form of feedback that still gets the message across. Transient results go in
                a toast, persistent context goes in a notification, and anything the user must answer goes in a dialog.
            </p>

            <h3 class="title is-5">
                Toasts
            </h3>
            <p class="block">
                Toasts confirm that something happened and then disappear. Prefer <code>useShow</code>
                (<code>showInfo</code> and <code>showError</code>) over calling Buefy directly: it gives us the
                agreed defaults of <code>is-top</code>, five seconds, and <code>is-info</code> for success and
                <code>is-danger</code> for failure, and it parses API errors into readable messages. Avoid putting an
                action in a toast, and do not use one for a message the user must not miss.
            </p>
            <div class="block">
                <div class="buttons">
                    <b-button
                        type="is-info"
                        @click="showInfo('The library was saved')">
                        Info toast
                    </b-button>
                    <b-button
                        type="is-danger"
                        @click="showError('The library could not be saved')">
                        Error toast
                    </b-button>
                </div>
            </div>

            <h3 class="title is-5">
                Notifications
            </h3>
            <p class="block">
                Notifications stay on the page and explain the state of what the user is looking at, for instance a
                read-only library or a failed import. Place them directly above the content they describe, and keep them
                to one or two sentences with a link to more detail if needed.
            </p>
            <b-notification type="is-primary">
                This is a primary notification. <a href="#">And a link.</a>
            </b-notification>
            <b-notification type="is-danger">
                This is a danger notification. <a href="#">And a link.</a>
            </b-notification>
            <b-notification type="is-info">
                This is an info notification. <a href="#">And a link.</a>
            </b-notification>
            <b-notification>
                This is a default notification. <a href="#">And a link.</a>
            </b-notification>
            <b-notification type="is-warning">
                This is a warning notification. <a href="#">And a link.</a>
            </b-notification>

            <h3 class="title is-5">
                Confirmations
            </h3>
            <p class="block">
                Confirm destructive or irreversible actions with <code>dialog.confirm</code>. Say what will be
                deleted or changed, use <code>type="is-danger"</code>, and label the confirm button with the verb rather
                than <i>Yes</i>. Do not confirm actions the user can simply undo.
            </p>
            <div class="block">
                <b-button
                    type="is-danger"
                    icon-left="trash-can"
                    @click="showDemoConfirm()">
                    Delete with confirmation
                </b-button>
            </div>

            <h3 class="title is-5">
                Loading
            </h3>
            <p class="block">
                Show progress wherever the user is waiting, and keep the indicator close to what is loading:
                <code>:loading</code> on the button that triggered the request, <code>:loading</code> on a table while
                its rows are fetched, the shared <code>Spinner</code> around a region that has nothing to show yet, and
                <code>b-loading</code> only for a genuinely blocking, page-wide wait. A spinning
                <code>loading</code> or <code>refresh</code> icon (add <code>custom-class="mdi-spin"</code>) works for
                inline, in-place refreshes.
            </p>

            <h3 class="title is-5">
                Tags
            </h3>
            <p class="block">
                Tags label state and classification in a compact way, mostly in tables. Keep the semantics consistent:
                <code>is-success</code> for approved or healthy, <code>is-warning</code> for pending or needs attention,
                <code>is-danger</code> for failed or deleted, <code>is-info</code> for neutral classification, and no
                type for a plain label. Tag text is a noun, not a sentence.
            </p>
            <div class="block">
                <div class="tags">
                    <b-tag type="is-success">
                        Approved
                    </b-tag>
                    <b-tag type="is-warning">
                        Draft
                    </b-tag>
                    <b-tag type="is-danger">
                        Failed
                    </b-tag>
                    <b-tag type="is-info">
                        Global
                    </b-tag>
                    <b-tag>
                        Plain
                    </b-tag>
                </div>
            </div>

            <h3 class="title is-5">
                Help and tooltips
            </h3>
            <p class="block">
                Use the shared <code>HelpIcon</code> to explain a field or a domain concept; it renders a consistent
                help icon with a tooltip and supports rich content. Use <code>b-tooltip</code> to name icon-only
                buttons and to expand truncated values. Do not hide information the user needs in order to act inside a
                tooltip, and prefer a field <code>message</code> for guidance that applies every time.
            </p>
            <div class="block">
                Sample field label
                <help-icon text="This is the kind of explanation that belongs in a help icon." />
            </div>
            <p class="is-flex is-flex-wrap-wrap is-gap-2 is-size-7 block">
                <a
                    href="https://buefy.org/documentation/toast"
                    target="_blank"
                    rel="noopener">Buefy: toast</a>
                <a
                    href="https://buefy.org/documentation/notification"
                    target="_blank"
                    rel="noopener">Buefy: notification</a>
                <a
                    href="https://buefy.org/documentation/dialog"
                    target="_blank"
                    rel="noopener">Buefy: dialog</a>
                <a
                    href="https://buefy.org/documentation/tag"
                    target="_blank"
                    rel="noopener">Buefy: tag</a>
            </p>

            <h2
                id="section-shared-components"
                class="title is-4 mt-6 section-header">
                Shared components
            </h2>
            <p class="block">
                These live in <code>src/shared/components</code> and already solve problems you are likely to hit. Use
                them instead of building a local variant; if one almost fits, extend it rather than forking it.
            </p>
            <table class="table is-narrow is-striped is-fullwidth block">
                <thead>
                    <tr>
                        <th>Component</th>
                        <th>Purpose</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="sharedComponent in sharedComponents"
                        :key="sharedComponent.name">
                        <td><code>{{ sharedComponent.name }}</code></td>
                        <td>{{ sharedComponent.purpose }}</td>
                    </tr>
                </tbody>
            </table>

            <h2
                id="section-references"
                class="title is-4 mt-6 section-header">
                External documentation
            </h2>
            <p class="block">
                This page is deliberately thin on component details. For props, slots, events and the full set of helper
                classes, go to the source:
            </p>
            <table class="table is-narrow is-striped is-fullwidth block">
                <thead>
                    <tr>
                        <th>Reference</th>
                        <th>Description</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="referenceLink in referenceLinks"
                        :key="referenceLink.url">
                        <td>
                            <a
                                :href="referenceLink.url"
                                target="_blank"
                                rel="noopener">{{ referenceLink.label }}</a>
                            <br>
                            <span class="has-text-grey is-size-7">{{ referenceLink.version }}</span>
                        </td>
                        <td>{{ referenceLink.description }}</td>
                    </tr>
                </tbody>
            </table>
            <p class="block">
                Keep those versions in mind when reading the docs: Buefy 3 is the Vue 3 line, and Bulma 1 changed
                several class names compared with older answers you may find online.
            </p>

            <h2
                id="section-iconography"
                class="title is-4 mt-6 section-header">
                Iconography
            </h2>
            <p class="block">
                We use Material Design Icons through <code>@mdi/font</code>. Any icon name from the
                <a
                    href="https://pictogrammers.com/library/mdi/"
                    target="_blank"
                    rel="noopener">MDI library</a>
                works, but prefer an icon that is already in use below: a consistent, small vocabulary is far more
                readable than a precise one. An icon repeats or replaces a label, it never carries meaning on its own,
                so an icon without a visible label needs a tooltip, and a clickable one needs an
                <code>aria-label</code> as well. Icons marked as deprecated here are on their way
                out; do not use them in new code. If you introduce an icon for a new concept, add it to this page.
            </p>
            <p class="block">
                Use colored icons to draw extra attention to important actions or statuses.
            </p>
            <p class="is-flex is-flex-wrap-wrap is-gap-2 is-size-7 block">
                <a
                    href="https://buefy.org/documentation/icon"
                    target="_blank"
                    rel="noopener">Buefy: icon</a>
            </p>
            <div class="grid is-col-min-8 block">
                <div
                    v-for="iconType in iconTypes"
                    :key="iconType.type"
                    class="cell is-flex is-align-items-flex-start p-2 has-background-light">
                    <b-icon
                        :type="iconType.type"
                        icon="plus-circle"
                        class="mr-2" />
                    <div>
                        <span class="is-block">type: {{ iconType.type }}</span>
                        <span class="is-block is-size-7 has-text-grey">{{ iconType.description }}</span>
                    </div>
                </div>
            </div>
            <p class="block">
                Sizes:
            </p>
            <div class="grid is-col-min-8 block">
                <div
                    v-for="iconSize in iconSizes"
                    :key="iconSize"
                    class="cell is-flex is-align-items-center p-2 has-background-light">
                    <b-icon
                        icon="plus-circle"
                        :size="iconSize"
                        class="mr-2" />
                    <span>size: {{ iconSize }}</span>
                </div>
            </div>
            <p class="block">
                Spinning icons are possible by adding the <code>mdi-spin</code> custom class to any icon.
            </p>
            <div class="block">
                <b-icon
                    icon="loading"
                    custom-class="mdi-spin" />
                <b-icon
                    icon="refresh"
                    custom-class="mdi-spin" />
                <b-icon
                    icon="cog"
                    custom-class="mdi-spin" />
            </div>
            <div
                v-for="iconGroup in iconographyIcons"
                :key="iconGroup.title"
                class="block">
                <h3 class="title is-5">
                    {{ iconGroup.title }}
                </h3>
                <p class="block">
                    {{ iconGroup.description }}
                </p>

                <div class="grid is-col-min-8">
                    <div
                        v-for="iconEntry in iconGroup.icons"
                        :key="iconEntry.icon"
                        class="cell is-flex is-align-items-flex-start p-2"
                        :class="iconEntry.deprecated ? 'has-text-grey-light' : 'has-background-light'">
                        <b-icon
                            :icon="iconEntry.icon"
                            class="mr-2" />
                        <div>
                            <span
                                class="is-block"
                                :class="{ 'is-strikeout': iconEntry.deprecated }">{{ iconEntry.icon }}</span>
                            <span class="is-block is-size-7 has-text-grey">{{ iconEntry.description }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <modal-base
        v-if="isModalActive"
        @on-cancel="isModalActive = false"
        @on-save="isModalActive = false">
        <template #modal-header>
            Modal demo
        </template>
        <template #modal-body>
            <p>
                The buttons in the modal footer are right-aligned. The cancel button is on the left and the affirmative button is on the right.
            </p>
        </template>
        <template #modal-footer>
            <b-field class="is-grouped-right">
                <p class="control">
                    <b-button
                        icon-left="close-circle"
                        @click="isModalActive = false">
                        Cancel
                    </b-button>
                </p>
                <p class="control">
                    <b-button
                        type="is-primary"
                        icon-left="check-circle"
                        @click="isModalActive = false">
                        Save
                    </b-button>
                </p>
            </b-field>
        </template>
    </modal-base>
</template>

<style scoped>
/* A hairline under each section heading, drawn with Bulma's own border colour. */
.section-header {
    border-bottom: 1px solid var(--bulma-border-weak);
}

/* Keep the section menu in view while reading, but only from Bulma's tablet
   breakpoint, where the columns sit side by side rather than stacked. */
@media screen and (min-width: 769px) {
    .section-menu {
        position: sticky;
        top: 0;
    }
}
</style>
